import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import * as DocumentTypeActions from './document-type.actions';
import { TableNames } from '../../services/utils/dexie.utils';
import { DexieService } from '../../services/dexie.service';
import { DocumentTypeService } from '../../services/document-type.service';
import { DocumentType } from '../../services/model';
import { selectDocumentTypes } from './document-type.selectors';

@Injectable({
  providedIn: 'root',
})
export class DocumentTypeEffects {
  loadDocumentTypes$;
  createDocumentType$;
  updateDocumentType$;
  deleteDocumentType$;
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly documentTypeService: DocumentTypeService,
    private readonly dexieService: DexieService
  ) {
    this.loadDocumentTypes$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentTypeActions.fetchDocumentTypes),
        switchMap(() =>
          this.dexieService.allItems<DocumentType>(TableNames.DOCUMENT_TYPE).pipe(
            catchError(() => of([]))
          )
        ),
        tap((documentTypes) => {
          if (documentTypes.length > 0) {
            this.store.dispatch(DocumentTypeActions.loadDocumentTypes({ documentTypes }));
          }
        }),
        switchMap(() => this.documentTypeService.getAll()),
        switchMap((documentTypes) =>
          this.dexieService.updateTable(TableNames.DOCUMENT_TYPE, documentTypes).pipe(
            map(() => documentTypes),
            catchError(() => of(documentTypes))
          )
        ),
        map((documentTypes) => DocumentTypeActions.loadDocumentTypes({ documentTypes })),
        catchError((error) => {
          console.error('Error loading document types:', error);
          return of(DocumentTypeActions.loadDocumentTypeFailed({ error: error.message || 'Failed to load document types' }));
        })
      )
    );

    this.createDocumentType$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentTypeActions.createDocumentType),
        switchMap(({ documentType }) => this.documentTypeService.create(documentType)),
        switchMap((documentType) =>
          this.dexieService.add(TableNames.DOCUMENT_TYPE, documentType).pipe(map(() => documentType))
        ),
        withLatestFrom(this.store.select(selectDocumentTypes)),
        map(([newDocumentType, documentTypes]) => {
          let currentDocumentTypes = documentTypes ?? [];
          currentDocumentTypes = [...currentDocumentTypes, newDocumentType];
          this.dexieService.addOrUpdateItems(TableNames.DOCUMENT_TYPE, currentDocumentTypes);
          return DocumentTypeActions.loadDocumentTypes({ documentTypes: [...currentDocumentTypes] });
        })
      )
    );

    this.updateDocumentType$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentTypeActions.updateDocumentType),
        switchMap(({ documentType }) => this.documentTypeService.update(documentType)),
        withLatestFrom(this.store.select(selectDocumentTypes)),
        map(([updatedDocumentType, documentTypes]) => {
          const currentDocumentTypes = documentTypes ?? [];
          const index = currentDocumentTypes.findIndex((doc) => doc.id === updatedDocumentType.id);
          if (index !== -1) {
            currentDocumentTypes[index] = updatedDocumentType;
          }
          this.dexieService.addOrUpdateItems(TableNames.DOCUMENT_TYPE, currentDocumentTypes);
          return DocumentTypeActions.loadDocumentTypes({ documentTypes: [...currentDocumentTypes] });
        })
      )
    );

    this.deleteDocumentType$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentTypeActions.deleteDocumentType),
        concatMap(({ id }) =>
          this.documentTypeService.delete(id).pipe(
            withLatestFrom(this.store.select(selectDocumentTypes)),
            map(([, types]) => {
              const documentTypes = (types ?? []).filter((d) => d.id !== id);
              this.dexieService.addOrUpdateItems(TableNames.DOCUMENT_TYPE, documentTypes);
              return DocumentTypeActions.loadDocumentTypes({ documentTypes });
            })
          )
        )
      )
    );
  }
}
